import { useState, useMemo } from 'react';

export const useSortableData = (items, config = null) => {
    const [sortConfig, setSortConfig] = useState(config);

    const sortedItems = useMemo(() => {
        // Only run logic if we have an array
        if (!Array.isArray(items)) return items;

        let sortableItems = [...items];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle null/undefined values by mapping them to empty string equivalent
                if (aValue === null || aValue === undefined) aValue = '';
                if (bValue === null || bValue === undefined) bValue = '';

                // If both are numbers, standard subtraction
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                    return 0;
                }

                // If string, use localeCompare for case-insensitivity
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    // Quick check if they are valid ISO date strings
                    const aDate = Date.parse(aValue);
                    const bDate = Date.parse(bValue);
                    if (!isNaN(aDate) && !isNaN(bDate) && isNaN(aValue)) {
                        if (aDate < bDate) return sortConfig.direction === 'ascending' ? -1 : 1;
                        if (aDate > bDate) return sortConfig.direction === 'ascending' ? 1 : -1;
                        return 0;
                    }

                    return sortConfig.direction === 'ascending'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                // Fallback comparison
                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [items, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (
            sortConfig &&
            sortConfig.key === key &&
            sortConfig.direction === 'ascending'
        ) {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return { items: sortedItems, requestSort, sortConfig };
};
